import { useState } from 'react'
import { useTranslation } from '@/hooks/use-translation'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Textarea } from '@/components/ui/textarea'
import { Survey, SurveyQuestion } from '@/features/forms-and-surveys/types'
import { getTranslatedSurveyTypeLabel } from '@/features/forms-and-surveys/utils'

interface FnsPreviewProps {
  data: Survey
  open: boolean
  onOpenChange: (open: boolean) => void
}

interface PreviewAnswers {
  [questionId: string]: string | string[] | number
}

export function FnsPreview({ data, open, onOpenChange }: FnsPreviewProps) {
  const [answers, setAnswers] = useState<PreviewAnswers>({})
  const { t } = useTranslation()

  const handleAnswerChange = (
    questionId: string,
    value: string | string[] | number
  ) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: value,
    }))
  }

  const handleSingleChoiceChange = (questionId: string, value: string) => {
    handleAnswerChange(questionId, value)
  }

  const handleMultipleChoiceChange = (
    questionId: string,
    option: string,
    checked: boolean
  ) => {
    const currentAnswers = (answers[questionId] as string[]) || []
    const newAnswers = checked
      ? [...currentAnswers, option]
      : currentAnswers.filter((ans) => ans !== option)
    handleAnswerChange(questionId, newAnswers)
  }

  const handleRatingChange = (questionId: string, value: number) => {
    handleAnswerChange(questionId, value)
  }

  const renderQuestion = (question: SurveyQuestion, index: number) => {
    const questionId = question.id || `question-${index}`
    const currentAnswer = answers[questionId]

    return (
      <Card key={questionId} className='mb-6 pt-6'>
        <CardContent>
          <div className='space-y-4'>
            {/* Question Header */}
            <div className='flex items-start justify-between'>
              <div className='flex-1'>
                <div className='mb-2 flex items-center gap-2'>
                  <span className='font-semibold text-primary'>
                    Q{index + 1}
                  </span>
                  <Badge variant='outline' className='text-xs'>
                    {getTranslatedSurveyTypeLabel(t, question.type || 'Text')}
                  </Badge>
                  {question.isRequired && (
                    <Badge variant='destructive' className='text-xs'>
                      {t('surveys.required')}
                    </Badge>
                  )}
                </div>
                <h3 className='text-lg font-medium'>{question.text}</h3>
              </div>
            </div>

            {/* Question Input */}
            <div className='space-y-3'>
              {question.type === 'Text' && (
                <Textarea
                  placeholder={t('surveys.enterAnswerPlaceholder')}
                  value={(currentAnswer as string) || ''}
                  onChange={(e) =>
                    handleAnswerChange(questionId, e.target.value)
                  }
                  rows={3}
                />
              )}

              {question.type === 'SingleChoice' && question.options && (
                <RadioGroup
                  value={(currentAnswer as string) || ''}
                  onValueChange={(value) =>
                    handleSingleChoiceChange(questionId, value)
                  }
                >
                  {question.options.map(
                    (option: string, optionIndex: number) => (
                      <div
                        key={optionIndex}
                        className='flex items-center space-x-2'
                      >
                        <RadioGroupItem
                          value={option}
                          id={`${questionId}-${optionIndex}`}
                        />
                        <Label
                          htmlFor={`${questionId}-${optionIndex}`}
                          className='text-sm'
                        >
                          {option}
                        </Label>
                      </div>
                    )
                  )}
                </RadioGroup>
              )}

              {question.type === 'MultipleChoice' && question.options && (
                <div className='space-y-2'>
                  {question.options.map(
                    (option: string, optionIndex: number) => (
                      <div
                        key={optionIndex}
                        className='flex items-center space-x-2'
                      >
                        <Checkbox
                          id={`${questionId}-${optionIndex}`}
                          checked={((currentAnswer as string[]) || []).includes(
                            option
                          )}
                          onCheckedChange={(checked) =>
                            handleMultipleChoiceChange(
                              questionId,
                              option,
                              checked as boolean
                            )
                          }
                        />
                        <Label
                          htmlFor={`${questionId}-${optionIndex}`}
                          className='text-sm'
                        >
                          {option}
                        </Label>
                      </div>
                    )
                  )}
                </div>
              )}

              {question.type === 'Rating' && (
                <div className='space-y-3'>
                  <div className='flex items-center justify-between text-sm text-muted-foreground'>
                    <span>{question.minValue || 1}</span>
                    <span>{question.maxValue || 5}</span>
                  </div>
                  <div className='flex flex-wrap items-center gap-2'>
                    {Array.from(
                      {
                        length:
                          (question.maxValue || 5) -
                          (question.minValue || 1) +
                          1,
                      },
                      (_, i) => {
                        const value = (question.minValue || 1) + i
                        return (
                          <Button
                            key={value}
                            type='button'
                            variant={
                              currentAnswer === value ? 'default' : 'outline'
                            }
                            size='sm'
                            onClick={() =>
                              handleRatingChange(questionId, value)
                            }
                            className='h-10 w-10'
                          >
                            {value}
                          </Button>
                        )
                      }
                    )}
                  </div>
                </div>
              )}

              {question.type === 'YesNo' && (
                <RadioGroup
                  value={(currentAnswer as string) || ''}
                  onValueChange={(value) =>
                    handleAnswerChange(questionId, value)
                  }
                >
                  <div className='flex items-center space-x-2'>
                    <RadioGroupItem value='Yes' id={`${questionId}-yes`} />
                    <Label htmlFor={`${questionId}-yes`}>
                      {t('common.yes')}
                    </Label>
                  </div>
                  <div className='flex items-center space-x-2'>
                    <RadioGroupItem value='No' id={`${questionId}-no`} />
                    <Label htmlFor={`${questionId}-no`}>{t('common.no')}</Label>
                  </div>
                </RadioGroup>
              )}

              {question.type === 'CTA' && (
                <div className='space-y-3'>
                  <p className='text-sm text-muted-foreground'>
                    {t('surveys.ctaRedirectMessage')} {question.actionUrl}
                  </p>
                  <Button
                    type='button'
                    variant='outline'
                    className='w-full'
                    onClick={() =>
                      question.actionUrl &&
                      window.open(question.actionUrl, '_blank')
                    }
                  >
                    {question.actionLabel || t('surveys.learnMore')}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='flex h-[90vh] max-w-2xl flex-col p-0'>
        <DialogHeader className='border-b px-6 py-4'>
          <DialogTitle>{t('surveys.surveyPreview')}</DialogTitle>
          <DialogDescription>
            {t('surveys.surveyPreviewDescription')}
          </DialogDescription>
        </DialogHeader>

        <div className='flex-1 overflow-y-auto px-6 py-4'>
          {/* Survey Header */}
          <Card className='mb-6'>
            <CardHeader>
              <CardTitle>{data.title}</CardTitle>
              {data.description && (
                <CardDescription>{data.description}</CardDescription>
              )}
            </CardHeader>
          </Card>

          {/* Questions */}
          {data.questions && data.questions.length > 0 ? (
            data.questions.map((question, index) =>
              renderQuestion(question, index)
            )
          ) : (
            <div className='py-8 text-center text-muted-foreground'>
              <p>{t('surveys.noQuestionsAddedYet')}</p>
            </div>
          )}
        </div>

        <div className='flex items-center justify-between border-t bg-muted/20 px-6 py-4'>
          <p className='text-sm text-muted-foreground'>
            {t('surveys.previewNotSaved')}
          </p>
          <Button onClick={() => onOpenChange(false)}>
            {t('common.close')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
